// components/ListingCard.jsx
import React, { useMemo, useState, useRef, useEffect } from "react";
import { Card, Button, Tooltip, Tag, Popover } from "antd";
import { LinkOutlined, ClockCircleOutlined, ShoppingCartOutlined, LineChartOutlined } from "@ant-design/icons";
import ModalCarousel from "./ModalCarousel";
import { money, clampWords, getImagesForItem } from "../utils";

// ---- Hooptie Popover styles ---------------------------------------------------
const HOOPTIE_POPOVER_INNER = {
  background: "#ffffff",
  color: "#1a1f36",
  borderRadius: 14,
  padding: 16,
  boxShadow: "0px 8px 20px rgba(0, 32, 128, 0.08)",
  border: "1px solid #e5eaf0",
  maxWidth: 360,
  lineHeight: 1.5,
  fontSize: 14,
};
const HOOPTIE_POPOVER_TITLE = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: "#0a6cff",
};
const HOOPTIE_POPOVER_UL = { margin: "10px 0 8px", paddingLeft: 18 };
const HOOPTIE_POPOVER_HIGHLIGHT = { color: "#00a2ff", fontWeight: 500 };

// Convert long description into short bullet list
function bulletize(text) {
  if (!text) return [];
  const normalized = text.replace(/\s+/g, " ").trim();
  const parts = normalized
    .split(/[\n•\-–—]|(?<=[.!?])\s+/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.filter((s) => s.length <= 120).slice(0, 6);
}

// Helper to detect source from url
function getSourceTag(url) {
  if (!url) return null;
  if (url.includes("facebook.com")) return { label: "Facebook", color: "#1877f2" };
  if (url.includes("craigslist.org")) return { label: "Craigslist", color: "#ff5700" };
  if (url.includes("cars.com")) return { label: "Cars.com", color: "#0a6cff" };
  if (url.includes("autotrader.com")) return { label: "Autotrader", color: "#f59e0b" };
  return null;
}

const ListingCard = ({ item, onViewInsights }) => {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const images = useMemo(() => getImagesForItem(item) || [], [item]);
  const img = images[0];

  const imgWrapRef = useRef(null);
  const [imgW, setImgW] = useState(0);
  
  useEffect(() => {
    if (!imgWrapRef.current) return;
    const ro = new window.ResizeObserver((entries) => {
      const w = entries?.[0]?.contentRect?.width || 0;
      setImgW(w);
    });
    ro.observe(imgWrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const desc = item?.description || item?.seller_description || "";
  const title =
    item?.title ||
    [item?.year, item?.make || item?.vehicle_make, item?.model, item?.trim]
      .filter(Boolean)
      .join(" ");

  const miles = item?.miles ?? item?.mileage;
  const milesText = typeof miles === "number" ? `${miles.toLocaleString()} mi` : "";

  const listedText = item?.listing_date
    ? (() => {
        // Parse UTC date and convert to user's local timezone
        const utcDate = new Date(item.listing_date);
        const now = new Date();
        
        // Calculate difference in milliseconds
        const diffMs = now.getTime() - utcDate.getTime();
        
        // Convert to different units
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        // Format based on time difference
        if (diffMinutes < 60) {
          return `Listed ${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
        } else if (diffHours < 24) {
          return `Listed ${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
        } else {
          return `Listed ${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
        }
      })()
    : "Listed recently";

  const vdpUrl = item?.vdp_url || item?.url;
  const sourceTag = getSourceTag(vdpUrl);

  // Build popover content from description - show full description
  const popoverContent = (
    <div>
      <h3 style={HOOPTIE_POPOVER_TITLE}>{title || "Vehicle details"}</h3>
      <div style={{ marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {desc}
      </div>
      {item?.warranty && (
        <div style={{ marginTop: 6 }}>
          Includes <span style={HOOPTIE_POPOVER_HIGHLIGHT}>{item.warranty}</span>
        </div>
      )}
    </div>
  );

  // Mobile View Component - Desktop-like layout
  const MobileView = () => (
    <Card
      bordered={false}
      hoverable
      className="listing-card-mobile"
      style={{
        minHeight: 480,
        borderRadius: 18,
        background: "linear-gradient(180deg, rgba(15,18,28,.94) 0%, rgba(9,12,20,.94) 100%)",
        border: "1px solid rgba(255,255,255,.06)",
        boxShadow: "0 16px 32px rgba(0,0,0,.38)",
        display: "flex",
        flexDirection: "column",
        padding: 0,
      }}
      bodyStyle={{ padding: 0 }}
    >
      {/* Image well */}
      <div
        ref={imgWrapRef}
        className="image-well"
        style={{
          width: "100%",
          height: 200,
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          overflow: "hidden",
          background: "#0b1220",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06)",
          cursor: images.length ? "pointer" : "default",
          position: "relative",
        }}
        onClick={() => images.length > 0 && setOpen(true)}
        title={images.length ? "Click to view gallery" : undefined}
      >
        {img ? (
          <img
            src={img}
            alt={title || "vehicle"}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div
            style={{
              color: "#8EA0C6",
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: 0.2,
              textTransform: "uppercase",
            }}
          >
            No images
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className="title-section"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "12px 18px 0 18px",
          flex: 1,
        }}
      >
        <div
          className="title-text"
          style={{
            color: "#E9EEFF",
            fontWeight: 800,
            fontSize: 18,
            lineHeight: 1.25,
            marginBottom: 0,
          }}
          title={title}
        >
          {title || "Untitled listing"}
        </div>

        <div style={{ display: "flex", alignItems: "center", marginBottom: 0 }}>
          <div style={{ color: "#34d399", fontSize: 20, fontWeight: 800 }}>
            {money(item?.asking_price ?? item?.price)}
          </div>
          {milesText && (
            <div style={{ color: "#9aa6b2", fontSize: 13, marginLeft: 12 }}>{milesText}</div>
          )}
          <div style={{ flex: 1 }} />
          {sourceTag && (
            <Tag
              color={sourceTag.color}
              style={{
                fontWeight: 600,
                background: sourceTag.color,
                color: "#fff",
                border: "none",
                marginLeft: 8,
                padding: "2px 10px",
                fontSize: 13,
                borderRadius: 8,
              }}
            >
              {sourceTag.label}
            </Tag>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 0 }}>
          <div className="location-text" style={{ color: "#a5b4fc", fontSize: 14 }}>
            {item?.location || item?.city_state || ""}
          </div>
          <div style={{ color: "#9fb4d2", fontSize: 14 }}>{listedText}</div>
        </div>

        {/* Description */}
        {desc ? (
          <Popover
            placement="topLeft"
            trigger={["click"]}
            content={popoverContent}
            overlayInnerStyle={HOOPTIE_POPOVER_INNER}
            overlayClassName="hooptie-popover"
          >
            <div
              className="description-text"
              style={{
                color: "#8EA0C6",
                fontSize: 15,
                lineHeight: 1.5,
                overflow: "hidden",
                marginBottom: 6,
                whiteSpace: "pre-line",
                wordBreak: "break-word",
                maxHeight: "5em",
                cursor: "pointer",
              }}
            >
              {clampWords(desc, 40)}{" "}
              <span style={{ color: "#0a6cff", fontWeight: 600 }}>(more)</span>
            </div>
          </Popover>
        ) : null}

        {/* Divider */}
        <div
          style={{
            height: 1,
            background: "rgba(255,255,255,.08)",
            margin: "8px 0 8px",
          }}
        />

        {/* METRICS SECTION */}
        <div
          className="metrics-section"
          style={{
            display: "flex",
            alignItems: "stretch",
            justifyContent: "space-between",
            flexWrap: "nowrap",
            overflow: "visible",
            marginBottom: 8,
            gap: 6,
          }}
        >
          {/* Active Count */}
          <div
            className="metric-item"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flex: "1 1 0",
              minWidth: 0,
              minHeight: 44,
              padding: "6px 8px",
              background: "rgba(255, 255, 255, 0.02)",
              borderRadius: 8,
              border: "1px solid rgba(255, 255, 255, 0.06)",
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: "rgba(245, 158, 11, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LineChartOutlined style={{ fontSize: 12, color: "#f59e0b" }} />
            </div>
            <div style={{ lineHeight: 1.2, minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 10, color: "#9aa6b2", fontWeight: 500, marginBottom: 2 }}>
                Act. Count
              </div>
              <div style={{ fontWeight: 700, color: "#E9EEFF", fontSize: 14 }}>
                {item?.active_count ?? "—"}
              </div>
            </div>
          </div>

          {/* Sold Count */}
          <div 
            className="metric-item"
            style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 6, 
              flex: "1 1 0",
              minWidth: 0,
              minHeight: 44,
              padding: "6px 8px",
              background: "rgba(255, 255, 255, 0.02)",
              borderRadius: 8,
              border: "1px solid rgba(255, 255, 255, 0.06)",
            }}
          >
            <div
              className="metric-icon"
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: "rgba(52, 211, 153, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ShoppingCartOutlined style={{ fontSize: 12, color: "#34d399" }} />
            </div>
            <div style={{ lineHeight: 1.2, minWidth: 0, flex: 1 }}>
              <div className="metric-label" style={{ fontSize: 10, color: "#9aa6b2", fontWeight: 500, marginBottom: 2 }}>
                Sold Count
              </div>
              <div className="metric-value" style={{ fontWeight: 700, color: "#E9EEFF", fontSize: 14 }}>
                {item?.sold_count ?? "—"}
              </div>
            </div>
          </div>

          {/* Sold DOM */}
          <div
            className="metric-item"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flex: "1 1 0",
              minWidth: 0,
              minHeight: 44,
              padding: "6px 8px",
              background: "rgba(255, 255, 255, 0.02)",
              borderRadius: 8,
              border: "1px solid rgba(255, 255, 255, 0.06)",
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: "rgba(165, 180, 252, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ClockCircleOutlined style={{ fontSize: 12, color: "#a5b4fc" }} />
            </div>
            <div style={{ lineHeight: 1.2, minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 10, color: "#9aa6b2", fontWeight: 500, marginBottom: 2 }}>
                Sold DOM
              </div>
              <div style={{ fontWeight: 700, color: "#E9EEFF", fontSize: 14 }}>
                {item?.sold_dom ?? "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Divider under metrics */}
        <div
          style={{
            height: 1,
            background: "rgba(255,255,255,.08)",
            margin: "8px 0 8px",
          }}
        />

        <div style={{ flex: 1 }} />
      </div>

      {/* Footer actions */}
      <div
        className="footer-actions"
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 10,
          padding: "12px 18px",
          alignItems: "center",
          justifyContent: "flex-start",
          flexWrap: "nowrap",
        }}
      >
        {vdpUrl && (
          <Button
            icon={<LinkOutlined />}
            href={vdpUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontWeight: 600,
              color: "#a5b4fc",
              background: "none",
              border: "none",
              boxShadow: "none",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: 0,
            }}
          >
            Original Listing
          </Button>
        )}

        <Button
          type="default"
          className="view-insights-btn"
          onClick={() => {
            onViewInsights?.(item);
          }}
          style={{
            background: "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)",
            border: "none",
            height: 28,
            paddingInline: 12,
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3), 0 2px 8px rgba(139, 92, 246, 0.2)",
            transition: "all 0.3s ease",
            flexShrink: 0,
            marginLeft: 8,
            color: "#ffffff",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(59, 130, 246, 0.4), 0 4px 16px rgba(139, 92, 246, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3), 0 2px 8px rgba(139, 92, 246, 0.2)";
          }}
        >
          View Insights
        </Button>
      </div>
    </Card>
  );

  // Desktop View Component
  const DesktopView = () => (
      <Card
        bordered={false}
        hoverable
      className="listing-card-desktop"
        style={{
          minHeight: 480,
          borderRadius: 18,
        background: "linear-gradient(180deg, rgba(15,18,28,.94) 0%, rgba(9,12,20,.94) 100%)",
          border: "1px solid rgba(255,255,255,.06)",
          boxShadow: "0 16px 32px rgba(0,0,0,.38)",
          display: "flex",
          flexDirection: "column",
          padding: 0,
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* Image well */}
        <div
          ref={imgWrapRef}
          className="image-well"
          style={{
            width: "100%",
            height: 200,
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
            overflow: "hidden",
            background: "#0b1220",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06)",
            cursor: images.length ? "pointer" : "default",
            position: "relative",
          }}
          onClick={() => images.length > 0 && setOpen(true)}
          title={images.length ? "Click to view gallery" : undefined}
        >
          {img ? (
            <img
              src={img}
              alt={title || "vehicle"}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div
              style={{
                color: "#8EA0C6",
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: 0.2,
                textTransform: "uppercase",
              }}
            >
              No images
            </div>
          )}
        </div>

        {/* Content */}
        <div
          className="title-section"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            padding: "12px 18px 0 18px",
            flex: 1,
          }}
        >
          <div
            className="title-text"
            style={{
              color: "#E9EEFF",
              fontWeight: 800,
              fontSize: 18,
              lineHeight: 1.25,
              marginBottom: 0,
            }}
            title={title}
          >
            {title || "Untitled listing"}
          </div>

          <div style={{ display: "flex", alignItems: "center", marginBottom: 0 }}>
            <div style={{ color: "#34d399", fontSize: 20, fontWeight: 800 }}>
              {money(item?.asking_price ?? item?.price)}
            </div>
            {milesText && (
              <div style={{ color: "#9aa6b2", fontSize: 13, marginLeft: 12 }}>{milesText}</div>
            )}
            <div style={{ flex: 1 }} />
            {sourceTag && (
              <Tag
                color={sourceTag.color}
                style={{
                  fontWeight: 600,
                  background: sourceTag.color,
                  color: "#fff",
                  border: "none",
                  marginLeft: 8,
                  padding: "2px 10px",
                  fontSize: 13,
                  borderRadius: 8,
                }}
              >
                {sourceTag.label}
              </Tag>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 0 }}>
            <div style={{ color: "#a5b4fc", fontSize: 14 }}>
              {item?.location || item?.city_state || ""}
            </div>
            <div style={{ color: "#9fb4d2", fontSize: 14 }}>{listedText}</div>
          </div>

          {/* Description */}
          {desc ? (
            <Popover
              placement="topLeft"
              trigger={["click"]}
              content={popoverContent}
              overlayInnerStyle={HOOPTIE_POPOVER_INNER}
              overlayClassName="hooptie-popover"
            >
              <div
                style={{
                  color: "#8EA0C6",
                  fontSize: 15,
                  lineHeight: 1.5,
                  overflow: "hidden",
                  marginBottom: 6,
                  whiteSpace: "pre-line",
                  wordBreak: "break-word",
                  maxHeight: "5em",
                  cursor: "pointer",
                }}
              >
                {clampWords(desc, 40)}{" "}
                <span style={{ color: "#0a6cff", fontWeight: 600 }}>(more)</span>
              </div>
            </Popover>
          ) : null}

          {/* Divider */}
          <div
            style={{
              height: 1,
              background: "rgba(255,255,255,.08)",
              margin: "8px 0 8px",
            }}
          />

          {/* METRICS SECTION */}
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              justifyContent: "space-between",
              flexWrap: "nowrap",
              overflow: "visible",
              marginBottom: 8,
              gap: 6,
            }}
          >
            {/* Active Count */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flex: "1 1 0",
                minWidth: 0,
                minHeight: 44,
                padding: "6px 8px",
                background: "rgba(255, 255, 255, 0.02)",
                borderRadius: 8,
                border: "1px solid rgba(255, 255, 255, 0.06)",
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: "rgba(245, 158, 11, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <LineChartOutlined style={{ fontSize: 12, color: "#f59e0b" }} />
              </div>
              <div style={{ lineHeight: 1.2, minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 10, color: "#9aa6b2", fontWeight: 500, marginBottom: 2 }}>
                  Act. Count
                </div>
                <div style={{ fontWeight: 700, color: "#E9EEFF", fontSize: 14 }}>
                  {item?.active_count ?? "—"}
                </div>
              </div>
            </div>

            {/* Sold Count */}
            <div 
              style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 6, 
                flex: "1 1 0",
                minWidth: 0,
                minHeight: 44,
                padding: "6px 8px",
                background: "rgba(255, 255, 255, 0.02)",
                borderRadius: 8,
                border: "1px solid rgba(255, 255, 255, 0.06)",
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: "rgba(52, 211, 153, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ShoppingCartOutlined style={{ fontSize: 12, color: "#34d399" }} />
              </div>
              <div style={{ lineHeight: 1.2, minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 10, color: "#9aa6b2", fontWeight: 500, marginBottom: 2 }}>
                  Sold Count
                </div>
                <div style={{ fontWeight: 700, color: "#E9EEFF", fontSize: 14 }}>
                  {item?.sold_count ?? "—"}
                </div>
              </div>
            </div>

            {/* Sold DOM */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flex: "1 1 0",
                minWidth: 0,
                minHeight: 44,
                padding: "6px 8px",
                background: "rgba(255, 255, 255, 0.02)",
                borderRadius: 8,
                border: "1px solid rgba(255, 255, 255, 0.06)",
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: "rgba(165, 180, 252, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ClockCircleOutlined style={{ fontSize: 12, color: "#a5b4fc" }} />
              </div>
              <div style={{ lineHeight: 1.2, minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 10, color: "#9aa6b2", fontWeight: 500, marginBottom: 2 }}>
                  Sold DOM
                </div>
                <div style={{ fontWeight: 700, color: "#E9EEFF", fontSize: 14 }}>
                  {item?.sold_dom ?? "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Divider under metrics */}
          <div
            style={{
              height: 1,
              background: "rgba(255,255,255,.08)",
              margin: "8px 0 8px",
            }}
          />

          <div style={{ flex: 1 }} />
        </div>

        {/* Footer actions */}
        <div className="listing-footer">
          {vdpUrl && (
            <Button
              icon={<LinkOutlined />}
              href={vdpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="original-listing-btn"
              style={{
                fontWeight: 600,
                color: "#a5b4fc",
                background: "none",
                border: "none",
                boxShadow: "none",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: 0,
              }}
            >
              Original Listing
            </Button>
          )}

          <Button
            type="default"
            className="view-insights-btn"
            onClick={() => {
              onViewInsights?.(item);
            }}
            style={{
              background: "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)",
              border: "none",
              height: 28,
              paddingInline: 12,
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3), 0 2px 8px rgba(139, 92, 246, 0.2)",
              transition: "all 0.3s ease",
              flexShrink: 0,
              marginLeft: 8,
              color: "#ffffff",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(59, 130, 246, 0.4), 0 4px 16px rgba(139, 92, 246, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3), 0 2px 8px rgba(139, 92, 246, 0.2)";
            }}
          >
            View Insights
          </Button>
        </div>
      </Card>
    );

  return (
    <>
      {/* Scoped CSS so the popover ARROW matches the white box */}
      <style>
        {`
          .hooptie-popover .ant-popover-arrow:before,
          .hooptie-popover .ant-popover-arrow:after {
            background: #ffffff !important;
            box-shadow: 0px 8px 20px rgba(0, 32, 128, 0.08) !important;
            border: 1px solid #e5eaf0 !important;
          }
          /* AntD v5 sets a CSS var for inner background—ensure it's white */
          .hooptie-popover .ant-popover-inner {
            background: #ffffff !important;
            border-radius: 14px !important;
            border: 1px solid #e5eaf0 !important;
            box-shadow: 0px 8px 20px rgba(0, 32, 128, 0.08) !important;
          }
        `}
      </style>

      {isMobile ? <MobileView /> : <DesktopView />}

      {/* Modal Carousel */}
      <ModalCarousel
        open={open}
        onClose={() => setOpen(false)}
        images={images}
        title={title}
        meta={{
          price: item?.asking_price ?? item?.price,
          miles: milesText,
          listed: listedText,
          link: vdpUrl,
        }}
      />
    </>
  );
};

export default ListingCard;

// Add responsive CSS for 13-inch laptops
const responsiveStyles = `
  @media (max-width: 1366px) {
    .listing-card-desktop,
    .listing-card-mobile {
      min-height: 420px !important;
    }
    
    .listing-card-desktop .ant-card-body,
    .listing-card-mobile .ant-card-body {
      padding: 12px !important;
    }
    
    .listing-card-desktop .metrics-section,
    .listing-card-mobile .metrics-section {
      gap: 4px !important;
      margin-bottom: 6px !important;
    }
    
    .listing-card-desktop .metric-item,
    .listing-card-mobile .metric-item {
      padding: 4px 6px !important;
      min-height: 36px !important;
    }
    
    .listing-card-desktop .metric-icon,
    .listing-card-mobile .metric-icon {
      width: 20px !important;
      height: 20px !important;
    }
    
    .listing-card-desktop .metric-label,
    .listing-card-mobile .metric-label {
      font-size: 9px !important;
      margin-bottom: 1px !important;
    }
    
    .listing-card-desktop .metric-value,
    .listing-card-mobile .metric-value {
      font-size: 12px !important;
    }
    
    .listing-card-desktop .footer-actions,
    .listing-card-mobile .footer-actions {
      padding: 8px 12px !important;
      gap: 8px !important;
    }
    
    .listing-card-desktop .view-insights-btn,
    .listing-card-mobile .view-insights-btn {
      height: 32px !important;
      padding-inline: 16px !important;
      font-size: 13px !important;
    }
  }
  
  @media (max-width: 1200px) {
    .listing-card-desktop,
    .listing-card-mobile {
      min-height: 400px !important;
    }
    
    .listing-card-desktop .image-well,
    .listing-card-mobile .image-well {
      height: 160px !important;
    }
    
    .listing-card-desktop .title-section,
    .listing-card-mobile .title-section {
      padding: 8px 12px !important;
    }
    
    .listing-card-desktop .title-text,
    .listing-card-mobile .title-text {
      font-size: 14px !important;
      line-height: 1.3 !important;
    }
    
    .listing-card-desktop .location-text,
    .listing-card-mobile .location-text {
      font-size: 12px !important;
    }
    
    .listing-card-desktop .description-text,
    .listing-card-mobile .description-text {
      font-size: 13px !important;
      line-height: 1.4 !important;
      max-height: 4em !important;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = responsiveStyles;
  document.head.appendChild(styleSheet);
}
